import { Empty, Form, message, Row } from "antd";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useGetOrganization } from "../../../api/v1";
import { PageScreen } from "../../../types/search";
import { TemplateField } from "../../../types/template";
import { SectionLoader } from "../../atoms/SectionLoader/SectionLoader";
import { ButtonGroup, ButtonGroupProps } from "../../molecules/Button/ButtonGroup";
import { DynamicFields, transformDynamicFields } from "../DynamicFields";
import { useUpdateFinancialDimensions } from "./api";

type FinancialDimensionFormsProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => Promise<void> | void;
  propertyId?: string;
  propertyFinancialDimensions?: Record<string, unknown>;
  screen?: PageScreen;
};

const transformCustomFieldDefaultValue = (field: TemplateField, value: unknown) => {
  if (field.type !== "select" && value && typeof value === "object" && "value" in (value as Record<string, unknown>)) {
    return (value as Record<string, unknown>).value;
  }

  if (field.type !== "select" || !value || typeof value !== "object") {
    return value;
  }

  const selectValue = value as Record<string, unknown>;
  if ("key" in selectValue && "value" in selectValue) {
    return {
      value: selectValue.key,
      label: String(selectValue.value ?? ""),
    };
  }

  return value;
};

const getFieldDisabledState = (value: unknown) => {
  if (!value || typeof value !== "object" || !("isDisabled" in (value as Record<string, unknown>))) return undefined;

  const { isDisabled } = value as Record<string, unknown>;
  return typeof isDisabled === "boolean" ? isDisabled : undefined;
};

export const FinancialDimensionsForm = (props: FinancialDimensionFormsProps) => {
  const { isOpen, onClose, onSubmitSuccess, propertyId, propertyFinancialDimensions, screen } = props;

  const { organization, isLoadingOrganization } = useGetOrganization({
    params: { filterkeys: "financialDimensions.property" },
    isEnabled: isOpen,
  });

  const { updateFinancialDimensionsRequest, isPendingUpdateFinancialDimensions } = useUpdateFinancialDimensions({
    onSuccess: async () => {
      onClose();
      await onSubmitSuccess?.();
    },
    onError: (error) => {
      message.error(error?.response?.data?.error ?? "Failed to update financial dimensions");
    },
  });

  const dynamicFieldsTemplate = useMemo(() => {
    const template = (organization?.financialDimensions?.property as TemplateField[]) ?? [];
    const hasPropertyFinancialDimensions =
      propertyFinancialDimensions && Object.keys(propertyFinancialDimensions).length > 0;

    if (!hasPropertyFinancialDimensions) return template;

    return template.map((field) => {
      const propertyFieldValue = propertyFinancialDimensions[field.name];
      const propertyFieldDisabled = getFieldDisabledState(propertyFieldValue);

      return {
        ...field,
        default: transformCustomFieldDefaultValue(field, propertyFieldValue),
        isDisabled: propertyFieldDisabled ?? field.isDisabled,
      };
    });
  }, [organization?.financialDimensions, propertyFinancialDimensions]);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm();

  const defaultProps = { control, setValue };

  const onSubmit = async (data) => {
    if (!propertyId) {
      message.error("Unable to update financial dimensions fields");
      return;
    }

    const financialDimensions = transformDynamicFields(data, { selectOptionAsObject: true });

    await updateFinancialDimensionsRequest({
      propertyId,
      payload: { financialDimensions },
    });
  };

  const submitPrimaryButton: ButtonGroupProps["primaryButton"] = {
    label: "Submit",
    htmlType: "submit",
    loading: isPendingUpdateFinancialDimensions,
  };

  const submitSecondaryButton: ButtonGroupProps["secondaryButton"] = {
    label: "Cancel",
    onClick: onClose,
    disabled: isPendingUpdateFinancialDimensions,
  };

  const hasPropertyFinancialDimensions =
    Boolean(propertyFinancialDimensions) && Object.keys(propertyFinancialDimensions).length > 0;

  return (
    <SectionLoader isLoading={isLoadingOrganization} containerStyle={{ minHeight: "14rem" }}>
      {screen !== "property" && !hasPropertyFinancialDimensions ? (
        <Empty
          description="No financial dimensions found. Please add financial dimensions on property first."
          style={{ margin: "6rem" }}
        />
      ) : dynamicFieldsTemplate.length === 0 ? (
        <Empty description="No custom fields available" style={{ margin: "6rem" }} />
      ) : (
        <Form layout="vertical" className="bf-main" onFinish={handleSubmit(onSubmit)}>
          <Row gutter={16}>
            <DynamicFields template={dynamicFieldsTemplate} {...defaultProps} errors={errors} colSpan={12} />
            {screen === "property" && (
              <ButtonGroup
                primaryButton={submitPrimaryButton}
                secondaryButton={submitSecondaryButton}
                style={{ marginTop: "1rem", paddingInline: "0.5rem" }}
              />
            )}
          </Row>
        </Form>
      )}
    </SectionLoader>
  );
};
